/**
 * Phase 11.0 — Controlled Promotional Plays display helpers.
 *
 * Public emotional surfaces (featured rails, leaderboard, profile,
 * share cards, weekly Top 3, Spotlight pages) should render the
 * UNIFIED display plays number. Admin / A&R / metrics dashboards
 * keep using raw `play_count`.
 *
 * All helpers fall back gracefully when `display_plays` isn't present
 * yet (legacy cached responses) — they return the original
 * `play_count` so nothing breaks during rollout.
 */

export const getDisplayPlays = (item = {}) => {
  if (item == null) return 0;
  if (typeof item.display_plays === "number") return item.display_plays;
  // Legacy / admin paths — fall back to raw real plays.
  const real = Number(item.play_count ?? item.plays ?? 0);
  return Number.isFinite(real) ? real : 0;
};

export const getDisplayPlaysForArtist = (artist = {}) => {
  if (artist == null) return 0;
  // Phase 14.0 — different endpoints expose this field under slightly
  // different names: /charts/top-artists uses `display_plays` (artist-
  // level), while /discover/top-artists-weekly + /discover/rising-artists
  // use `total_display_plays`. Both have the same meaning. Fall back to
  // raw real plays for legacy cached responses.
  if (typeof artist.total_display_plays === "number") return artist.total_display_plays;
  if (typeof artist.display_plays === "number") return artist.display_plays;
  const real = Number(artist.total_plays ?? artist.plays ?? 0);
  return Number.isFinite(real) ? real : 0;
};

/**
 * Compact "1.2K" / "4.5M" style formatter used across the public
 * emotional surfaces. Always positive integer-like.
 */
export const formatDisplayPlays = (n) => {
  const v = Math.max(0, Number(n) || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(v);
};

/**
 * Coarse UX label, mirrors the backend's `momentum_badge()`.
 * Lets the frontend show a chip even before the backend label arrives
 * (e.g. optimistic UI on a fresh feature).
 */
export const getMomentumBadge = (item = {}) => {
  if (!item) return null;
  if (item.momentum_badge) return item.momentum_badge;
  const promo = Number(item.promotional_plays || 0);
  if (item.spotlight_position || promo >= 4000) return "🚀 Featured Push Active";
  if (item.status === "featured" && promo > 0) return "🔥 In Rotation";
  if (promo > 0) return "📈 Momentum Rising";
  return null;
};
